<template>
  <div>
    <div>
      <h4 class="mb-2 mt-5">Watch Folder</h4>
      <v-autocomplete
        class="mb-3"
        outlined
        dense
        :value="settings.location"
        @input="(val) => setLocation(val)"
        item-text="name"
        item-value="id"
        :items="driveFolders"
        label="File Location"
        @click:append-outer="listFolders"
        :append-outer-icon="loading ? 'mdi-loading mdi-spin' : 'mdi-reload'"
        clearable
      ></v-autocomplete>
    </div>

    <div>
      <h4 class="mb-2 mt-5">Webhook Settings</h4>

      <div v-if="!webhook?.id || !settings.webhookId">
        <v-select
          class="mb-3"
          outlined
          dense
          :value="selectedNode.settings.selectedEWACredential"
          @input="(val) => onUpdate(val, 'settings.selectedEWACredential')"
          item-text="name"
          item-value="id"
          :items="attachedEWACredentials"
          label="Selected EWACredential"
          clearable
        ></v-select>
        <v-btn
          small
          @click="createWebhook"
          :loading="createLoading"
          :disabled="
            !workflow?.id ||
            !settings.selectedEWACredential ||
            !settings.authenticationId
          "
        >
          Create Webhook
        </v-btn>
        <small class="d-block mt-2">
          <span class="font-weight-bold">Note:</span>
          Workflow needs to be saved before creating the webhook.
        </small>
      </div>

      <div class="d-flex flex-column" v-else>
        <div class="d-flex align-center justify-space-between mb-3">
          <div class="body-2">
            <span class="font-weight-bold">Expires At:</span>
            {{ webhookExpiresAt }}
            <v-chip
              :color="isWebhookExpired ? 'error' : 'success'"
              small
              class="ml-2"
            >
              {{ isWebhookExpired ? 'Expired' : 'Active' }}
            </v-chip>
          </div>
          <div>
            <v-tooltip bottom v-if="isWebhookExpired">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  @click="renewSubscription"
                  :disabled="renewLoading"
                  :loading="renewLoading"
                  color="primary"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Renew Subscription</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  icon
                  small
                  @click="deleteWebhook"
                  :disabled="deleteLoading"
                  :loading="deleteLoading"
                  color="error"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </template>
              <span>Delete Webhook</span>
            </v-tooltip>
          </div>
        </div>
      </div>
    </div>

    <div class="mb-3">
      <h4 class="mb-2 mt-5">Test Input (JSON)</h4>
      <v-textarea
        v-model="testInputValue"
        @input="onTestInputChange"
        outlined
        label="Test JSON Input"
        placeholder="Enter test JSON input for testing the connector..."
        rows="8"
        :error-messages="jsonError"
        class="json-editor"
      />
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { createNamespacedHelpers } from 'vuex';

const { mapActions: workflowActions } =
  createNamespacedHelpers('workflows/list');
const { mapActions: webhooksActions, mapGetters: webhookGetters } =
  createNamespacedHelpers('webhooks');
const { mapGetters: workflowDetailsGetters } =
  createNamespacedHelpers('workflows/details');
const { mapActions: notificationsActions } =
  createNamespacedHelpers('notifications');
const { mapGetters: credentialsGetters } =
  createNamespacedHelpers('credentials');

export default {
  name: 'incoming-google',
  props: {
    availableInputFields: { required: true },
  },
  data() {
    return {
      driveFolders: [],
      loading: false,
      renewLoading: false,
      deleteLoading: false,
      createLoading: false,
      testInputValue: "",
      jsonError: null,
    };
  },
  computed: {
    ...workflowDetailsGetters({
      selectedNode: 'SELECTED_NODE',
      workflow: 'WORKFLOW',
    }),
    ...webhookGetters({
      webhook: 'WEBHOOK',
    }),
    ...credentialsGetters({
      credentials: 'AUTHENTICATIONS',
    }),
    webhookExpiresAt() {
      const date = Number(this.webhook?.metadata?.expiresAt);
      return moment(date).format('L LTS');
    },
    isWebhookExpired() {
      if (!this.webhook?.metadata?.expiresAt) {
        return false;
      }
      return moment().isAfter(moment(this.webhook.metadata.expiresAt));
    },
    settings() {
      return this.selectedNode.settings;
    },
    attachedEWACredentials() {
      return (
        this.credentials?.filter(
          (cred) =>
            this.workflow.credentials?.includes(cred.id) &&
            cred.credential_type === 'externalWorkflowActivator',
        ) || []
      );
    },
  },
  watch: {
    selectedNode: {
      handler() {
        this.initializeTestInput();
      },
      immediate: true,
      deep: true,
    },
  },
  created() {
    if (this.settings.webhookId) {
      this.fetchWebhook({ id: this.settings.webhookId });
    }

    if (!this.settings.location || !this.settings.locationName) {
      return;
    }
    this.driveFolders.push({
      id: this.settings.location,
      name: this.settings.locationName,
    });
  },
  methods: {
    ...workflowActions(['googleAction']),
    ...webhooksActions(['fetchWebhook']),
    ...notificationsActions(['pushNotification', 'setError']),
    onUpdate(value, path) {
      this.$emit('update', value, path);
    },
    setLocation(location) {
      this.onUpdate(location, 'settings.location');
      const folder = this.driveFolders.find((item) => item.id === location);
      if (folder?.name) {
        this.onUpdate(folder.name, 'settings.locationName');
        this.onUpdate(folder.driveId, 'settings.driveId');
      } else {
        this.onUpdate(null, 'settings.locationName');
        this.onUpdate(null, 'settings.driveId');
      }
    },
    async listFolders() {
      if (!this.settings.authenticationId) {
        return;
      }
      this.loading = true;
      try {
        const data = {
          authenticationId: this.settings.authenticationId,
          action: 'list_folders',
        };
        const resp = await this.googleAction(data);

        if (resp && resp.result) {
          this.driveFolders = resp.result;
        }
      } finally {
        this.loading = false;
      }
    },
    getEwaCredLink() {
      const ewaCred = this.credentials.find(
        (cred) => cred.id === this.settings.selectedEWACredential,
      );
      if (!ewaCred) {
        this.setError('Selected EWACredential not found');
        return null;
      }
      const secretKey = ewaCred.credentials[0].value;
      const type = this.workflow.trigger;
      const workflowId = this.workflow.id;
      const host = location.host;
      return `https://${host}/proxy/workflows/${workflowId}/external-activators/${type}/run?x-external-api-key=${secretKey}`;
    },
    async createWebhook() {
      try {
        this.createLoading = true;
        const data = {
          authenticationId: this.settings.authenticationId,
          link: this.getEwaCredLink(),
          workflow_name: this.workflow.name,
          action: 'create_webhook',
          location: this.settings.location,
        };
        const resp = await this.googleAction(data);

        if (resp && resp.result) {
          await this.fetchWebhook({ id: resp.result.webhookId });
          this.onUpdate(resp.result.webhookId, 'settings.webhookId');
          this.onUpdate(resp.result.channelId, 'settings.channelId');
          this.pushNotification('Webhook created successfully');
        } else {
          this.setError('Failed to create webhook');
        }
      } catch (error) {
        this.setError('Failed to create webhook: ' + error.message);
      } finally {
        this.createLoading = false;
      }
    },
    async deleteWebhook() {
      try {
        this.deleteLoading = true;
        const data = {
          authenticationId: this.settings.authenticationId,
          webhookId: this.settings.webhookId,
          channelId: this.settings.channelId,
          action: 'delete_webhook',
          location: this.settings.location,
        };
        const resp = await this.googleAction(data);

        if (resp && resp.success) {
          this.onUpdate(null, 'settings.webhookId');
          this.onUpdate(null, 'settings.channelId');
          this.pushNotification('Webhook deleted successfully');
        } else {
          this.setError('Failed to delete webhook');
        }
      } catch (error) {
        this.setError('Failed to delete webhook: ' + error.message);
      } finally {
        this.deleteLoading = false;
      }
    },
    async renewSubscription() {
      try {
        this.renewLoading = true;
        const data = {
          authenticationId: this.settings.authenticationId,
          channelId: this.webhook.metadata.channelId,
          webhookId: this.settings.webhookId,
          location: this.settings.location,
          action: 'renew_subscription',
        };
        const resp = await this.googleAction(data);

        if (resp && resp.success) {
          this.onUpdate(resp.result.channelId, 'settings.channelId');
          await this.fetchWebhook({ id: this.settings.webhookId });
          this.pushNotification('Subscription renewed successfully');
        } else {
          this.setError('Failed to renew subscription');
        }
      } catch (error) {
        this.setError('Failed to renew subscription: ' + error.message);
      } finally {
        this.renewLoading = false;
      }
    },

    initializeTestInput() {
      if (this.selectedNode?.settings?.testInput) {
        try {
          this.testInputValue =
            typeof this.selectedNode.settings.testInput === 'string'
              ? this.selectedNode.settings.testInput
              : JSON.stringify(this.selectedNode.settings.testInput, null, 2);
        } catch (error) {
          this.testInputValue = "";
        }
      } else {
        this.testInputValue = "";
      }
      this.jsonError = null;
    },

    onTestInputChange(value) {
      this.validateAndUpdateJson(value);
    },

    validateAndUpdateJson(value) {
      if (!value || value.trim() === "") {
        this.jsonError = null;
        this.onUpdate(null, "settings.testInput");
        return;
      }

      try {
        const parsedJson = JSON.parse(value);
        this.jsonError = null;
        this.onUpdate(parsedJson, "settings.testInput");
      } catch (error) {
        let errorMessage = "Invalid JSON syntax: ";

        if (error.message.includes("Unexpected token")) {
          if (error.message.includes("Unexpected token }")) {
            errorMessage += 'Extra closing brace "}" - check for missing commas or quotes';
          } else if (error.message.includes("Unexpected token ]")) {
            errorMessage += 'Extra closing bracket "]" - check array structure';
          } else if (error.message.includes("Unexpected token ,")) {
            errorMessage += 'Extra comma "," - remove trailing commas';
          } else if (error.message.includes("Unexpected string")) {
            errorMessage += "Missing comma between properties or invalid quotes";
          } else {
            errorMessage += "Unexpected character - check quotes, commas, and brackets";
          }
        } else if (error.message.includes("Unexpected end")) {
          errorMessage += 'Incomplete JSON - missing closing brace "}" or bracket "]"';
        } else if (error.message.includes("Expected property name")) {
          errorMessage += 'Property names must be in double quotes "propertyName"';
        } else {
          errorMessage += error.message;
        }

        this.jsonError = errorMessage;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.json-editor {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}
</style>
